import React from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { formatMoney, isNegativeMoney, isZeroMoney } from '../lib/money'
import { formatPercent, shareOf } from '../lib/overview'
import type { CostOverviewTotals } from '../types'

interface Props { totals: CostOverviewTotals; currency: string; locale: string }

/**
 * 1b-C spec §4: four figures of the selected scope; the variance turns red once the forecast exceeds the budget.
 * Potential changes spec §3.4: the forecast card names the part of the estimates the forecast contains, when there is one.
 */
const OverviewStats: React.FC<Props> = ({ totals, currency, locale }) => {
  const { t } = useTranslation()
  const money = (v: string) => formatMoney(v, { locale, currency, whole: true })
  const share = (v: string) => {
    const p = shareOf(v, totals.revisedBudget)
    return p === null ? null : t('CostControl_overview_share', { percent: formatPercent(p, locale) })
  }
  const cards = [
    { key: 'revisedBudget', label: t('CostControl_col_revisedBudget'), value: money(totals.revisedBudget), sub: null as string | null, danger: false, hint: null as string | null },
    { key: 'committed', label: t('CostControl_col_committed'), value: money(totals.committed), sub: share(totals.committed), danger: false, hint: null },
    { key: 'actual', label: t('CostControl_col_actual'), value: money(totals.actual), sub: share(totals.actual), danger: false,
      hint: isZeroMoney(totals.unallocatedActual) ? null : t('CostControl_overview_unallocated', { amount: money(totals.unallocatedActual) }),
    },
    {
      key: 'projectedFinalCost', label: t('CostControl_col_projectedFinalCost'), value: money(totals.projectedFinalCost),
      sub: t('CostControl_overview_variance', { amount: money(totals.variance) }), danger: isNegativeMoney(totals.variance),
      hint: isZeroMoney(totals.potentialInForecast) ? null : t('CostControl_overview_potential', { amount: money(totals.potentialInForecast) }),
    },
  ]
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((c) => (
        <Card key={c.key} className="p-4">
          <p className="text-sm text-muted-foreground">{c.label}</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{c.value}</p>
          {c.sub && <p className={cn('mt-1 text-xs', c.danger ? 'text-destructive' : 'text-muted-foreground')}>{c.sub}</p>}
          {c.hint && <p className="mt-0.5 text-xs text-muted-foreground">{c.hint}</p>}
        </Card>
      ))}
    </div>
  )
}
export default OverviewStats
